import {Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output} from '@angular/core';
import {CodaltComponent} from '../../codalt/codalt.component';
import {Utils} from '../../../utils.class';
import {Entity} from '../../../classes/entity';
import {Ttm} from '../../../classes/ttm';
import {Subscription} from 'rxjs';
import {ConfirmDialogService} from '../../../services/confirm-dialog-service/confirm-dialog.service';
import {EntityService} from '../../../services/entity.service';
import * as turf from '@turf/turf';
import {Point, Position} from '@turf/helpers';
import {Feature} from 'geojson';
import {GoogleMap} from '@angular/google-maps';
import {MatDialog} from '@angular/material/dialog';
import {TtmService} from '../../../services/ttm.service';
import {Project} from '../../../classes/project';
import {TtmType} from '../../../classes/ttmtype';
import _ from 'lodash';
import LatLngLiteral = google.maps.LatLngLiteral;
import MapMouseEvent = google.maps.MapMouseEvent;


@Component({
    selector: 'app-map-mutator',
    templateUrl: './map-mutator.component.html',
    styleUrl: './map-mutator.component.scss'
})
export class MapMutatorComponent extends CodaltComponent implements OnInit, OnChanges {


    movePoints: { lat: number; lng: number; }[];
    movingKeepGnd = false;
    movingEntity: Entity;
    movingTtm: Ttm;
    movingTtmInitial: Ttm;
    movingInitialLocation: LatLngLiteral;
    cancelingMove = false;
    savingMove = false;
    movingEntity$: Subscription;
    ttmMoveAllowed: boolean;
    addingTtm = false;
    addingTtmType: TtmType;
    sortedTtmTypes: TtmType[] = [];
    savingNewTtm = false;
    placementPosition: LatLngLiteral;
    private addingMouseMove$: Subscription;
    private addingClick$: Subscription;
    @Input() viewState: {
        ttms: boolean;
    }
    @Input() mobileControls: boolean;
    @Input() selectedTtm: Ttm;
    @Input() googleMap: GoogleMap;
    @Input() filteredTtms: Ttm[];
    @Input() mergeTtms: Function;
    @Input() splitTtm: Function;
    @Input() changeCenter: Function;
    @Input() project: Project;
    @Input() selectedDrawingIds: number[];
    @Input() ttmTypes: TtmType[];
    @Output() zoomToTtm = new EventEmitter<Ttm>();
    @Output() ttmAdded = new EventEmitter<Ttm>();

    constructor(private ttmService: TtmService,
                private entityService: EntityService,
                private confirmDialog: ConfirmDialogService,
                private dialog: MatDialog) {
        super();
    }

    @HostListener('document:keydown.escape', ['$event'])
    escKey() {
        this.cancelAddTtm();
    }

    ngOnInit(): void {

    }

    ngOnChanges() {
        this.sortedTtmTypes = _.sortBy(this.ttmTypes ?? [], 'priority');
        this.ttmMoveAllowed = this.selectedTtm?.entities?.filter(e => !!e.line)?.length === 1 || this.selectedTtm?.entities?.length === 1;
    }

    cancelMove() {
        return new Promise((resolve, reject) => {
            if (this.cancelingMove || this.savingMove) {
                reject();
            } else if (this.movingEntity) {
                this.confirmDialog.confirm('Verplaatsen annuleren',
                    'Je bent nog een maatregel aan het verplaatsen. Wil je daarmee stoppen?',
                    'Verplaatsen afbreken', 'Doorgaan met verplaatsen').then(() => {
                    this.undoMove();
                    resolve(true);
                }, () => {
                    reject();
                });
            } else {
                resolve(true);
            }
        });
    }

    saveMove() {
        if (this.cancelingMove || this.savingMove) {
            return;
        }

        const saved = () => {
            this.movingEntity = null;
            this.movingEntity$?.unsubscribe();
            this.movePoints = null;
            this.movingTtm = null;
            this.movingTtmInitial = null;
            this.movingKeepGnd = false;
            this.savingMove = false;
        };

        if (this.movingTtm) {
            this.subscriptions.add(this.entityService.saveEntities(this.movingTtm.entities).subscribe(() => {
                saved();
            }, () => {
                this.savingMove = false;
            }));
        } else if (this.movingEntity) {
            this.subscriptions.add(this.entityService.saveEntity(this.movingEntity).subscribe(() => {
                saved();
            }, () => {
                this.savingMove = false;
            }));
        }
    }

    updatePoint(movePoint: { lat: number, lng: number }, evt: MapMouseEvent) {
        movePoint.lat = evt.latLng.lat();
        movePoint.lng = evt.latLng.lng();
        this.movingEntity.line.coordinates = this.movePoints.map(mp => [mp.lng, mp.lat]);
        this.movingEntity.line = _.cloneDeep(this.movingEntity.line);
        // If movePoint is the last point, update the gnd coordinates
        if (this.movePoints.at(-1) === movePoint) {
            const initialEntity = this.movingTtmInitial.entities.find(e => e.id === this.movingEntity.id);
            const [lastLng, lastLat] = initialEntity.line.coordinates.at(-1);
            const offsetLng = movePoint.lng - lastLng;
            const offsetLat = movePoint.lat - lastLat;

            this.movingEntity.gnd.coordinates = initialEntity.gnd.coordinates.map(coordinate => {
                let [lng, lat] = coordinate;
                lng += offsetLng;
                lat += offsetLat;
                return [lng, lat];
            });

            this.movingEntity.gnd = {...this.movingEntity.gnd};
        }
    }

    undoMove() {
        if (this.cancelingMove || this.savingMove || !this.movingEntity) {
            return;
        }
        this.cancelingMove = true;
        this.movingEntity$?.unsubscribe();
        if (this.movingTtmInitial) {
            this.movingTtm.entities.forEach(movingEntity => {
                Object.assign(movingEntity, this.movingTtmInitial.entities.find(e => e.id === movingEntity.id));
                movingEntity.updateBbox?.();
                movingEntity.updateBboxText?.();
            });
            this.movingEntity = null;
            this.movePoints = null;
            this.movingTtm = null;
            this.movingTtmInitial = null;
            this.movingKeepGnd = false;
            this.cancelingMove = false;
        } else {
            this.subscriptions.add(this.entityService.getEntity(this.movingEntity.id).subscribe(entityResponse => {
                Object.assign(this.movingEntity, entityResponse.data);
                this.movingEntity.updateBbox?.();
                this.movingEntity.updateBboxText?.();
                this.movingEntity = null;
                this.movePoints = null;
                this.movingTtm = null;
                this.movingKeepGnd = false;
                this.cancelingMove = false;
            }, () => {
                this.cancelingMove = false;
            }));
        }
    }

    removePoint(point: { lat: number; lng: number; }) {
        this.movePoints.splice(this.movePoints.indexOf(point), 1);
        this.movingEntity.line.coordinates = this.movePoints.map(mp => [mp.lng, mp.lat]);
        this.movingEntity.line = _.cloneDeep(this.movingEntity.line);
    }

    private setNewLocation(entity: Entity, center: { lat: number; lng: number; }) {
        entity.gnd = Utils.latLngGeo(entity.gnd, center);

        if (!entity.line) { // TODO remove when conversion is completed
            entity.line = entity.pole;
        }
        if (entity.line) {
            const lineStartAndEnd = turf.featureCollection([turf.point(entity.line.coordinates[0]), turf.point(entity.line.coordinates[entity.line.coordinates.length - 1])]);
            const endLine = turf.nearestPoint(
                turf.point(entity.gnd.coordinates[0] as Position),
                lineStartAndEnd
            );
            const startLine = JSON.stringify(lineStartAndEnd.features[0].geometry) == JSON.stringify(endLine.geometry) ? lineStartAndEnd.features[1].geometry : lineStartAndEnd.features[0].geometry;
            const newLine = [];

            const anchorPointGnd = turf.nearestPoint(turf.point(startLine.coordinates as Position), turf.featureCollection(entity.gnd.coordinates.map(coord => turf.point(coord))));

            // Calculate the bearing based the GND of 2 coordinates, otherwise on the pole + 90 degrees
            let bearing: number;
            if (entity.gnd.coordinates.length === 2) {
                const point1 = turf.point(entity.gnd.coordinates[0] as Position);
                const point2 = turf.point(entity.gnd.coordinates[1] as Position);
                bearing = turf.bearing(point1, point2);
                newLine.push(startLine.coordinates);
            } else if (entity.pole?.coordinates[0]) {
                const point1 = turf.point(entity.pole.coordinates[0] as Position);
                const point2 = turf.point(entity.pole.coordinates[1] as Position);
                bearing = turf.bearing(point1, point2);
                bearing += 90;
                newLine.push(entity.pole.coordinates[0]);
            } else {
                bearing = (450 - entity.rotation) % 360;
                newLine.push(startLine.coordinates);
            }
            if (bearing) {
                // Check whether the current bearing moves away from the gnd or closer to the gnd
                const firstPointAfterStart = turf.destination(startLine, 5, bearing, {units: 'meters'});
                if (turf.distance(firstPointAfterStart, turf.point(entity.gnd.coordinates[0] as Position)) > turf.distance(startLine, turf.point(entity.gnd.coordinates[0] as Position))) {
                    // When it moves away, add 180 degrees
                    bearing += 180;
                }
                let point = turf.point(startLine.coordinates);

                const bboxPolygon = turf.bboxPolygon([entity.bbox.coordinates[0][0], entity.bbox.coordinates[0][1], entity.bbox.coordinates[1][0], entity.bbox.coordinates[1][1]]);

                const entityPolygon = entity.polygon ? turf.polygon([entity.polygon.coordinates as any as Position[]]) : bboxPolygon;
                const entityPolygonBuffered = turf.buffer(entityPolygon, 3, {units: 'meters'});
                const entityBbox = turf.bbox(entityPolygon);
                let bearedAnchor, bearedOppositeAnchor: Feature<Point>;
                let bearedIntersects, bearedOppositeIntersects, preferOpposite: boolean;
                let firstRound = true;
                const maxSize = Math.min(
                    500,
                    Math.max(
                        turf.distance(turf.point([entityBbox[0], entityBbox[1]]), turf.point([entityBbox[2], entityBbox[1]]), {units: 'meters'}),
                        turf.distance(turf.point([entityBbox[0], entityBbox[1]]), turf.point([entityBbox[0], entityBbox[3]]), {units: 'meters'})
                    )
                );

                let distance = 3;
                let doesIntersect = true;
                do {
                    distance++;
                    const toTheSide = turf.destination(startLine, distance, bearing, {units: 'meters'});
                    const up = turf.destination(toTheSide, maxSize, bearing + 90, {units: 'meters'});
                    const checkLine = turf.lineString([toTheSide.geometry.coordinates, up.geometry.coordinates]);
                    doesIntersect = turf.booleanIntersects(checkLine, entityPolygonBuffered);
                } while (doesIntersect);

                do {
                    distance += maxSize / 100;
                    point = turf.destination(startLine, distance, bearing, {units: 'meters'});
                    bearedAnchor = turf.destination(anchorPointGnd.geometry.coordinates, 7, bearing - 180, {units: 'meters'});
                    bearedOppositeAnchor = turf.destination(anchorPointGnd.geometry.coordinates, distance, bearing, {units: 'meters'});

                    const bearedLine = turf.lineString([point.geometry.coordinates, bearedAnchor.geometry.coordinates]);
                    const oppositeBearedLine = turf.lineString([point.geometry.coordinates, bearedOppositeAnchor.geometry.coordinates]);

                    bearedIntersects = turf.booleanIntersects(bearedLine, entityPolygonBuffered);
                    bearedOppositeIntersects = turf.booleanIntersects(oppositeBearedLine, entityPolygonBuffered);

                    preferOpposite = turf.distance(point.geometry.coordinates, bearedAnchor) > turf.distance(point.geometry.coordinates, bearedOppositeAnchor);

                    if (distance === 50 && firstRound) {
                        distance -= 45;
                        firstRound = false;
                    }
                } while (bearedOppositeIntersects && bearedIntersects && distance < maxSize);

                if (bearedIntersects) {
                    do {
                        distance -= +maxSize / 100;
                        const newBearedOppositeAnchor = turf.destination(anchorPointGnd.geometry.coordinates, distance, bearing, {units: 'meters'});
                        const oppositeBearedLine = turf.lineString([point.geometry.coordinates, newBearedOppositeAnchor.geometry.coordinates]);
                        bearedOppositeIntersects = turf.booleanIntersects(oppositeBearedLine, entityPolygonBuffered);
                        if (!bearedOppositeIntersects) {
                            bearedOppositeAnchor = newBearedOppositeAnchor;
                        }
                    } while (!bearedOppositeIntersects && distance > 5);
                }

                newLine.push(point.geometry.coordinates);

                if (bearedIntersects) {
                    newLine.push(bearedOppositeAnchor.geometry.coordinates);
                } else {
                    newLine.push(bearedAnchor.geometry.coordinates);
                }

                newLine.push(anchorPointGnd.geometry.coordinates);

                entity.line.coordinates = newLine;

                entity.line = _.cloneDeep(entity.line);
            } else {
                console.warn('Bearing could not be calculated', entity);
            }
        }
    }

    ttmMoveTotal() {
        this.movingEntity$?.unsubscribe();
        this.movingTtm = this.selectedTtm;
        this.movingEntity = this.selectedTtm.entities[0];
        this.movingTtmInitial = _.cloneDeep(this.selectedTtm);


        this.subscriptions.add(this.entityService.getForTtm(this.movingTtm.id).subscribe(entityResponse => {
            entityResponse.data.forEach(entity => {
                const currentEntity = this.movingTtm.entities.find(e => e.id === entity.id);
                Object.assign(currentEntity, entity);
            });

            this.zoomToTtm.emit(this.movingTtm);
            this.movingInitialLocation = {lng: this.googleMap.getCenter().lng(), lat: this.googleMap.getCenter().lat()}//Utils.geoLatLng(this.movingEntity.gnd);

            this.movingEntity$ = this.googleMap.centerChanged.subscribe(() => {
                const newCenter = this.googleMap.getCenter();

                const offsetLng = newCenter.lng() - this.movingInitialLocation.lng;
                const offsetLat = newCenter.lat() - this.movingInitialLocation.lat;

                this.movingTtm.entities.forEach((entity, index) => {
                    const initialEntity = this.movingTtmInitial.entities[index];

                    if (initialEntity) {
                        // Update bbox coordinates based on offset
                        entity.bbox.coordinates = initialEntity.bbox.coordinates.map(point => ([
                            point[0] + offsetLng,
                            point[1] + offsetLat
                        ]));
                        let updateLine = false;
                        if (entity.gnd?.coordinates) {
                            if (this.movingKeepGnd) {
                                if (initialEntity.gnd) {
                                    this.setNewLocation(entity, Utils.geoLatLng(initialEntity.gnd));
                                }
                            } else {
                                updateLine = true;
                                entity.gnd.coordinates = initialEntity.gnd.coordinates.map(x => {
                                    let c = _.cloneDeep(x);
                                    c[0] = c[0] + offsetLng;
                                    c[1] = c[1] + offsetLat;
                                    return c;
                                });
                            }
                        }
                        if (updateLine && entity.line?.coordinates) {
                            entity.line.coordinates = initialEntity.line.coordinates.map(x => {
                                let c = _.cloneDeep(x);
                                c[0] = c[0] + offsetLng;
                                c[1] = c[1] + offsetLat;
                                return c;
                            });
                        }
                        if (entity.pole?.coordinates) {
                            entity.pole.coordinates = initialEntity.pole.coordinates.map(x => {
                                let c = _.cloneDeep(x);
                                c[0] = c[0] + offsetLng;
                                c[1] = c[1] + offsetLat;
                                return c;
                            });
                        }

                        entity.bbox = _.cloneDeep(entity.bbox);
                        entity.gnd = _.cloneDeep(entity.gnd);
                        if (updateLine) {
                            entity.line = _.cloneDeep(entity.line);
                        }
                        entity.pole = _.cloneDeep(entity.pole);
                        entity.updateBbox?.();
                        entity.updateBboxText?.();
                    }
                });
            });
        }));
    }


    ttmMoveFree() {
        if (this.movingEntity || !this.selectedTtm?.entities[0]?.line) {
            return;
        }
        this.movingTtm = this.selectedTtm;
        this.zoomToTtm.emit(this.selectedTtm);
        this.subscriptions.add(this.entityService.getEntity(this.selectedTtm.entities[0].id).subscribe(entityResponse => {
            Object.assign(this.selectedTtm.entities[0], entityResponse.data);
            this.movingEntity = this.selectedTtm.entities[0];
            this.movingTtmInitial = _.cloneDeep(this.selectedTtm);

            this.subscriptions.add(this.entityService.getEntity(this.movingEntity.id).subscribe(entityResponse => {
                Object.assign(this.movingEntity, entityResponse.data);
                this.movePoints = this.movingEntity.line.coordinates.map((c, i) => {
                    return {lat: c[1], lng: c[0]};
                });
            }));
        }))
    }

    navigate() {
        const firstEntity = this.selectedTtm.entities[0];
        if (firstEntity) {
            window.open('https://maps.google.com/?q=' + firstEntity.gnd.coordinates[0][1] + ',' + firstEntity.gnd.coordinates[0][0], '_blank');
        }
    }

    ttmMove() {
        if (!this.ttmMoveAllowed) {
            return;
        }
        this.zoomToTtm.emit(this.selectedTtm);
        this.movingEntity$?.unsubscribe();
        const entitiesWithLine = this.selectedTtm.entities.filter(e => !!e.line);
        if (entitiesWithLine.length === 1) {
            this.movingEntity = entitiesWithLine[0];
        } else {
            this.movingEntity = this.selectedTtm.entities[0];
        }
        this.subscriptions.add(this.entityService.getEntity(this.movingEntity.id).subscribe(entityResponse => {
            Object.assign(this.movingEntity, entityResponse.data);
            this.changeCenter(Utils.geoLatLng(this.movingEntity.gnd));
            this.movingInitialLocation = Utils.geoLatLng(this.movingEntity.gnd);
            this.movingEntity$ = this.googleMap.centerChanged.subscribe(() => {
                const newCenter = this.googleMap.getCenter();
                this.setNewLocation(this.movingEntity, {lat: newCenter.lat(), lng: newCenter.lng()});
            });
        }));
    }

    startAddTtm(ttmType: TtmType) {
        this.stopAddingListeners();
        this.addingTtmType = ttmType;
        this.addingTtm = true;
        this.addingMouseMove$ = this.googleMap.mapMousemove.subscribe((evt: MapMouseEvent) => {
            this.placementPosition = {lat: evt.latLng.lat(), lng: evt.latLng.lng()};
        });
        this.addingClick$ = this.googleMap.mapClick.subscribe((evt: MapMouseEvent) => {
            if (!evt.latLng) {
                return;
            }
            const location = {lat: evt.latLng.lat(), lng: evt.latLng.lng()};
            this.stopAddingListeners();
            this.saveNewTtm(location);
        });
    }

    onPlacementMarkerClick(evt: MapMouseEvent) {
        if (!this.addingTtm || !this.placementPosition) {
            return;
        }
        const location = {...this.placementPosition};
        this.stopAddingListeners();
        this.saveNewTtm(location);
    }

    cancelAddTtm() {
        this.stopAddingListeners();
        this.addingTtmType = null;
        this.addingTtm = false;
        this.placementPosition = null;
    }

    private stopAddingListeners() {
        this.addingMouseMove$?.unsubscribe();
        this.addingClick$?.unsubscribe();
        this.addingMouseMove$ = null;
        this.addingClick$ = null;
        this.addingTtm = false;
        this.placementPosition = null;
    }

    private saveNewTtm(location: LatLngLiteral) {
        if (this.savingNewTtm) {
            return;
        }
        this.savingNewTtm = true;
        this.subscriptions.add(
            this.ttmService.addCustomTtm(this.project.id, this.selectedDrawingIds, location, this.addingTtmType.key).subscribe({
                next: (response) => {
                    this.savingNewTtm = false;
                    this.ttmAdded.emit(response.data);
                },
                error: () => {
                    this.savingNewTtm = false;
                }
            })
        );
    }


}
